/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconBarcode, IconDownload, IconTrash } from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import ListView from "../../../components/common/ListView";
import {
  useFetchShipmentInventoryDataByLocation,
  useLocationBarcodeVerifyMutation,
  useShipmentInventoryAddMutation,
  useShipmentInventoryDeleteMutation,
  useShipmentInventoryVerifyMutation,
} from "../../../hooks/inventoryHooks";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import { BarcodeInput } from "../BarcodeInput";
import NoDataFound from "../../../components/common/NoDataFound";

export const useBarcodeScanner = (onScan: (code: string) => void) => {
  useEffect(() => {
    let buffer = "";
    let lastKeyTime = 0;
    let timeout: NodeJS.Timeout;

    const handleKeydown = (e: KeyboardEvent) => {
      const now = Date.now();

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (buffer.length > 0) {
          onScan(buffer.trim());
          buffer = "";
        }
        return;
      }

      if (/^[\x20-\x7E]$/.test(e.key)) {
        if (now - lastKeyTime > 200) buffer = "";
        buffer += e.key;
        lastKeyTime = now;
        clearTimeout(timeout);
        timeout = setTimeout(() => (buffer = ""), 500);
      }
    };

    window.addEventListener("keydown", handleKeydown, true);
    return () => {
      window.removeEventListener("keydown", handleKeydown, true);
      clearTimeout(timeout);
    };
  }, [onScan]);
};

export default function InventoryCheckTab() {
  const [pageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [searchFor] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");
  const [locationShowId, setLocationShowId] = useState<string>("");
  const [awbNumber, setAwbNumber] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isShelfVerified, setIsShelfVerified] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [open, setOpen] = useState(false);

  const { data, refetch } = useFetchShipmentInventoryDataByLocation(
    locationId || "",
    pageNo,
    limit,
    searchFor,
  );

  const { mutateAsync: verifyLocationBarcode } =
    useLocationBarcodeVerifyMutation();
  const { mutate: verifyShipmentInventory } =
    useShipmentInventoryVerifyMutation();
  const { mutate: deleteShipmentInventory } =
    useShipmentInventoryDeleteMutation();
  const { mutate: addShipmentInventory } = useShipmentInventoryAddMutation();

  useEffect(() => {
    if (locationId) refetch();
  }, [locationId]);

  const resetShelf = () => {
    setLocationShowId("");
    setLocationId("");
    setIsShelfVerified(false);
  };

  const handleVerifyBarcode = async (barcode: string): Promise<boolean> => {
    try {
      const res: any = await verifyLocationBarcode({ barcodeId: barcode });
      setLocationShowId(res);
      setLocationId(res);
      setIsShelfVerified(true);
      refetch();
      return true;
    } catch (error: any) {
      toast.error(error?.message || "Invalid shelf barcode");
      return false;
    }
  };

  useBarcodeScanner(async (barcode) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      if (!locationId) {
        const verified = await handleVerifyBarcode(barcode);
        if (verified) {
          toast.success(`📍 Location set: ${barcode}`);
        }
      } else {
        handleSendRequest(barcode);
      }
    } catch (error) {
      toast.error("Failed to process barcode");
    } finally {
      setIsProcessing(false);
    }
  });

  const handleSendRequest = (awb: string) => {
    verifyShipmentInventory(
      { awb, locationId },
      {
        onSuccess: (res) => {
          toast.success(res.message);
          refetch();
          if (res.message === "Unmatched") {
            setOpen(true);
            setAwbNumber(awb);
          }
        },
        onError: (error) => {
          toast.error(String(error));
        },
      },
    );
  };

  const handleDeleteFloor = () => {
    deleteShipmentInventory(
      { awb: awbNumber, locationId },
      {
        onSuccess: () => {
          toast.success("Unmatched shipment deleted successfully!");
          setOpenDelete(false);
          refetch();
        },
        onError: (error: any) =>
          toast.error(`Failed to delete shipment: ${error}`),
      },
    );
  };

  const handleAddRequest = () => {
    addShipmentInventory(
      { barcodeId: locationId, orderId: [awbNumber] },
      {
        onSuccess: () => {
          toast.success("Shipment Inventory added successfully");
          setOpen(false);
          refetch();
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      },
    );
  };

  return (
    <>
      <div className="rounded-3xl">
        <div className="flex items-center">
          <div className="bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px]">
            <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
              Inventory Check (Shelve Location: {locationShowId})
            </h1>
            <div className="flex items-center gap-2">
              {isShelfVerified ? (
                <button
                  onClick={resetShelf}
                  className="rounded-full flex items-center gap-1 px-4 py-1 bg-[#FBD037] border border-[#FBD037]"
                >
                  <IconBarcode size={18} />
                  Reset Shelf
                </button>
              ) : (
                <BarcodeInput
                  placeholder="Scan / paste shelve barcode"
                  onVerified={handleVerifyBarcode}
                  disabled={isProcessing || isShelfVerified}
                />
              )}
              <NormalSearch />
            </div>
          </div>
          <div className="relative ml-5">
            <button className="flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]">
              <IconDownload stroke={1.5} size={24} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 bg-[#F7F6F2] rounded-tl-none rounded-3xl gap-6 p-4 pt-0">
           {data?.inventoryMovement?.length > 0 ? (
          <ListView
            mailDivClassName="w-full rounded-3xl"
            data={data?.inventoryMovement}
            columns={[
              { label: "#", render: (_: any, i: number) => i + 1 },
              {
                label: "AWB",
                render: (c: any) => c.awb,
              },
              {
                label: "Shelf ID",
                render: (c: any) => c.shelve,
              },
              {
                label: "Status",
                render: (c: any) => (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => !c.isVerify && handleSendRequest(c?.awb)}
                      disabled={c.isVerify}
                      className={`rounded-full px-4 py-1 text-center ${
                        c.isVerify
                          ? "bg-[#20BF55] text-white cursor-default"
                          : "bg-[#FF4848] text-white cursor-pointer"
                      }`}
                    >
                      {c.isVerify ? "Matched" : "Match"}
                    </button>

                    {!c.isVerify && (
                      <button
                        className="p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setOpenDelete(true);
                          setAwbNumber(c.awb);
                        }}
                      >
                        <IconTrash strokeWidth={1.5} size={20} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
          />
            ) : (
            <NoDataFound  />
          )}

          {data?.inventoryMovement?.filter(
            (inventory: any) => inventory?.isVerify,
          )?.length > 0 ? (
            <ListView
              mailDivClassName="w-full rounded-3xl"
              data={data?.inventoryMovement?.filter(
                (inventory: any) => inventory?.isVerify,
              )}
              columns={[
                {
                  label: "AWB",
                  render: (c: any) => c.awb,
                },
              ]}
            />
          ) : (
            <NoDataFound  />
          )}
        </div>

        <MessageModal
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          title="Delete Inventory Shipment Confirmation!"
          subtitle="Are you sure you want to perform this action?"
          img={"/img/junk.png"}
          showFooter={true}
          closeButtonText="Cancel"
          actionButtonText="Confirm"
          actionButtonOnClick={handleDeleteFloor}
          size="md"
        />
        <MessageModal
          open={open}
          onClose={() => setOpen(false)}
          title="Update Shipment Inventory Confirmation!"
          subtitle={`Are you sure you want to add shipment in given Location ${locationShowId} ?`}
          img={"/img/junk.png"}
          showFooter={true}
          closeButtonText="Cancel"
          actionButtonText="Confirm"
          actionButtonOnClick={handleAddRequest}
          size="md"
        />
      </div>
    </>
  );
}
